// LiveEdge 채널 유형 카탈로그 — liveedge.server DtoDeviceExtentions.GetDto 의 channelInfo 와 같은 필드 이름·기본값.
// 명세의 channel 값을 Edge 에 넣을 때 그대로 넘기므로 이름을 바꾸면 안 된다. 값은 문자열 (enum 은 멤버 이름).
// 기본값은 LiveEdge 템플릿(ChannelService.GetDeviceTemplate 의 new XxxChannel()) 기준, interval 기본 1000.
// LiveEdge 가 저장 때 읽지 않는 필드(ModbusRTUoverEthernet.socketProtocolType, ToyoTStation·EdgeAgent.interval)는 뺐다.
// Custom 은 하위 유형 DATABASE·TCP 만 (LiveEdge 처리 범위). 칸은 평평하게 두고 보낼 때 중첩한다 (channelShape).

export type FieldKind = 'text' | 'int' | 'enum'

export interface ChannelField {
  key: string
  label: string
  kind: FieldKind
  defaultValue: string
  options?: string[]
  secret?: boolean
  when?: (channel: Record<string, string>) => boolean // 이 조건일 때만 보인다 (Custom DATABASE 칸 등)
}

// 시트의 "접속" 한 칸이 어떤 필드를 보여 주는지
export type EndpointKind = 'ip' | 'uri' | 'serial' | 'path' | 'folder'

// 태그 주소 형식 (LiveEdge 태그 CSV 양식 구분) — modbus 는 "국번-기능코드x주소", focas 는 "함수[,인자][@필드]"
export type AddressKind = 'plc' | 'modbus' | 'focas' | 'custom'

export interface ChannelDef {
  type: string // LiveEdge MonitorChannelType 이름
  label: string // LiveEdge [Description]
  endpoint: EndpointKind
  address: AddressKind
  addressHint: string
  fields: ChannelField[]
}

const SOCKET = ['TCP', 'UDP']
const LS_CPU = ['XGK', 'XGI', 'XGB', 'XGR', 'MasterK', 'GLOFA']
const STOP_BITS = ['None', 'One', 'Two', 'OnePointFive']
const PARITY = ['None', 'Odd', 'Even', 'Mark', 'Space']

const interval: ChannelField = { key: 'interval', label: '수집 주기 (ms)', kind: 'int', defaultValue: '1000' }
const ip: ChannelField = { key: 'ip', label: 'IP', kind: 'text', defaultValue: '' }
const port = (defaultValue: string): ChannelField => ({ key: 'port', label: '포트', kind: 'int', defaultValue })
const socketType: ChannelField = { key: 'socketType', label: '소켓', kind: 'enum', defaultValue: 'TCP', options: SOCKET }
const maxMemorySize: ChannelField = { key: 'maxMemorySize', label: '최대 메모리 크기', kind: 'int', defaultValue: '100' }
const lsCpu: ChannelField = { key: 'cpuType', label: 'CPU 타입', kind: 'enum', defaultValue: 'XGK', options: LS_CPU }
const serial: ChannelField[] = [
  { key: 'comPort', label: 'COM 포트', kind: 'text', defaultValue: 'COM1' },
  { key: 'baudRate', label: 'Baud rate', kind: 'int', defaultValue: '19200' },
  { key: 'dataBits', label: 'Data bits', kind: 'int', defaultValue: '8' },
  { key: 'stopBits', label: 'Stop bits', kind: 'enum', defaultValue: 'One', options: STOP_BITS },
  { key: 'parity', label: 'Parity', kind: 'enum', defaultValue: 'None', options: PARITY },
]

const PLC_HINT = 'PLC 디바이스 주소 (예: D2000, P0)'
const MODBUS_HINT = '국번-기능코드x주소 (예: 1-3x0 = 국번 1, Holding Register 0)'

// ip 는 기본값을 비워 둔다 (LiveEdge 템플릿은 127.0.0.1) — 명세에서는 빈 칸이 "아직 모름"으로 보이는 편이 낫다
export const CHANNELS: ChannelDef[] = [
  {
    type: 'ModbusTCPUDP', label: 'Modbus TCP/UDP', endpoint: 'ip', address: 'modbus', addressHint: MODBUS_HINT,
    fields: [ip, port('502'), socketType, maxMemorySize, interval],
  },
  {
    type: 'ModbusRTUoverEthernet', label: 'Modbus RTU over Ethernet', endpoint: 'ip', address: 'modbus', addressHint: MODBUS_HINT,
    fields: [ip, port('502'), maxMemorySize, interval],
  },
  {
    type: 'ModbusRTU', label: 'Modbus RTU', endpoint: 'serial', address: 'modbus', addressHint: MODBUS_HINT,
    fields: [...serial, maxMemorySize, interval],
  },
  {
    type: 'LsPlcFEnet', label: 'LS FEnet', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [ip, port('2004'), lsCpu, socketType, interval],
  },
  {
    type: 'LsPlcCnetOverTcp', label: 'LS Cnet Over TCP', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [ip, port('2004'), { key: 'station', label: '국번', kind: 'int', defaultValue: '1' }, lsCpu, socketType, interval],
  },
  {
    type: 'LsPlcCnet', label: 'LS Cnet', endpoint: 'serial', address: 'plc', addressHint: PLC_HINT,
    fields: [...serial, { key: 'station', label: '국번', kind: 'int', defaultValue: '1' }, lsCpu, interval],
  },
  {
    type: 'McProtocolEthernet', label: 'Mitsubishi MC Protocol Ethernet', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [
      ip, port('5002'),
      { key: 'networkNo', label: '네트워크 번호', kind: 'int', defaultValue: '0' },
      { key: 'plcStationNo', label: 'PLC 국번', kind: 'int', defaultValue: '255' },
      { key: 'pcStationNo', label: 'PC 국번', kind: 'int', defaultValue: '255' },
      socketType,
      { key: 'frameType', label: '프레임', kind: 'enum', defaultValue: 'FRAME_3E', options: ['FRAME_3E', 'FRAME_1E'] },
      { key: 'packetDataType', label: '데이터 형식', kind: 'enum', defaultValue: 'BINARY', options: ['BINARY', 'ASCII'] },
      interval,
    ],
  },
  {
    type: 'OpcUA', label: 'OPC UA', endpoint: 'uri', address: 'plc', addressHint: '노드 경로 (예: Diameter/DiameterX/Value)',
    fields: [
      { key: 'serverUri', label: '서버 URI', kind: 'text', defaultValue: '' },
      { key: 'authenticationType', label: '인증 방식', kind: 'enum', defaultValue: 'Anonymous', options: ['Anonymous', 'UserPassword'] },
      { key: 'account', label: '계정', kind: 'text', defaultValue: '' },
      { key: 'password', label: '비밀번호', kind: 'text', defaultValue: '', secret: true },
      { key: 'securityModeType', label: '보안 모드', kind: 'enum', defaultValue: 'None', options: ['None', 'Sign', 'SignAndEncrypt'] },
      {
        key: 'securityPolicyType', label: '보안 정책', kind: 'enum', defaultValue: 'None',
        options: ['None', 'Basic256', 'Basic128Rsa15', 'Basic256Sha256', 'Aes128_Sha256_RsaOaep', 'Aes256_Sha256_RsaPss'],
      },
      { key: 'messageEncodingType', label: '메시지 인코딩', kind: 'enum', defaultValue: 'Binary', options: ['Binary', 'Xml'] },
      interval,
    ],
  },
  {
    type: 'SiemensS7Comm', label: 'Siemens S7Comm', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [ip, port('102'), { key: 'cpuType', label: 'CPU 타입', kind: 'enum', defaultValue: 'S7_1200', options: ['S7_1200', 'S7_1500', 'S7_300', 'S7_400'] }, interval],
  },
  {
    type: 'OmronFins', label: 'OMRON Fins', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [
      ip, port('9600'), socketType,
      { key: 'cpuType', label: 'CPU 타입', kind: 'enum', defaultValue: 'CS_CJ_CP_NSJ', options: ['CS_CJ_CP_NSJ', 'CV_CVM'] },
      { key: 'sourceNetwork', label: 'Source Network', kind: 'int', defaultValue: '0' },
      { key: 'sourceNode', label: 'Source Node', kind: 'int', defaultValue: '251' },
      { key: 'sourceUnit', label: 'Source Unit', kind: 'int', defaultValue: '0' },
      { key: 'destinationNetwork', label: 'Destination Network', kind: 'int', defaultValue: '0' },
      { key: 'destinationNode', label: 'Destination Node', kind: 'int', defaultValue: '1' },
      { key: 'destinationUnit', label: 'Destination Unit', kind: 'int', defaultValue: '0' },
      interval,
    ],
  },
  {
    type: 'CimonPlcEthernet', label: 'Cimon PLC Ethernet', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [ip, port('10260'), socketType, interval],
  },
  {
    type: 'YokogawaHLLink', label: 'Yokogawa Higher-level Link', endpoint: 'ip', address: 'plc', addressHint: PLC_HINT,
    fields: [
      ip, port('12289'),
      { key: 'cpuNo', label: 'CPU 번호', kind: 'int', defaultValue: '1' },
      socketType,
      { key: 'packetType', label: '패킷 형식', kind: 'enum', defaultValue: 'BINARY', options: ['BINARY', 'ASCII'] },
      interval,
    ],
  },
  {
    type: 'CipEthernet', label: 'Common Industrial Protocol', endpoint: 'ip', address: 'plc', addressHint: '태그 이름',
    fields: [ip, port('44818'), interval],
  },
  {
    type: 'Focas', label: 'FANUC Focas', endpoint: 'ip', address: 'focas', addressHint: '함수[,인자…][@필드] (예: cnc_rdparam,6711,0,8@ldata) — 칸에서 자동완성',
    fields: [ip, port('8193'), interval],
  },
  {
    type: 'ToyoTStation', label: 'TOYO T-STATION Watcher', endpoint: 'path', address: 'plc', addressHint: '항목 주소',
    fields: [{ key: 'path', label: '경로', kind: 'text', defaultValue: 'C:\\inetpub\\wwwroot\\web\\DATA\\Current\\INJ001\\dat' }],
  },
  {
    type: 'Custom', label: 'Custom (DB·TCP 모듈)', endpoint: 'ip', address: 'custom', addressHint: '커스텀 모듈이 쓰는 항목 주소',
    fields: [
      { key: 'type', label: '하위 유형', kind: 'enum', defaultValue: 'DATABASE', options: ['DATABASE', 'TCP'] },
      { key: 'filePath', label: '모듈 경로', kind: 'text', defaultValue: '' },
      ip, port(''), interval,
      { key: 'databaseType', label: 'DB 종류', kind: 'text', defaultValue: '', when: c => (c.type || 'DATABASE') === 'DATABASE' },
      { key: 'database', label: 'DB 이름', kind: 'text', defaultValue: '', when: c => (c.type || 'DATABASE') === 'DATABASE' },
      { key: 'user', label: '계정', kind: 'text', defaultValue: '', when: c => (c.type || 'DATABASE') === 'DATABASE' },
    ],
  },
  {
    type: 'EdgeAgent', label: 'Edge Agent', endpoint: 'folder', address: 'custom', addressHint: '항목 주소',
    fields: [{ key: 'folderKey', label: '폴더 키 ({AgentId}:{FolderId})', kind: 'text', defaultValue: '' }],
  },
]

const BY_TYPE = new Map(CHANNELS.map(c => [c.type, c]))

export const channelDef = (type: string): ChannelDef | undefined => BY_TYPE.get(type)

// LiveEdge TagDataType 이름 (값 순서)
export const DATA_TYPES = ['Bit', 'Word', 'UWord', 'Bcd', 'Ascii', 'CrossAscii', 'CrossWord', 'Float', 'CrossFloat', 'Real', 'Time', 'TimeSec', 'DateTime']
